import React from 'react';
import {
  Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography, Alert,
} from '@mui/material';
import dayjs from 'dayjs';
import InvButton from '../InvButton';
import { IPD_COLORS, ipdCardSx } from '../../theme';

function ReviewRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75 }}>
      <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: IPD_COLORS.textPrimary, textAlign: 'right' }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

function formatDateTime(value) {
  return value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '';
}

export default function RfqPublishReviewDialog({
  open,
  onClose,
  onPublish,
  loading,
  error,
  formId,
  rfqNumber,
  title,
  typeLabel,
  issueDate,
  submissionDeadline,
  minimumQuotes,
  validLineCount,
  vendorCount,
  isOpenBid,
  attachmentCount,
  itemLines = [],
}) {
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      disableScrollLock={false}
      sx={{ zIndex: 14000 }}
      PaperProps={{ sx: { ...ipdCardSx, color: IPD_COLORS.textPrimary, minWidth: 420, maxWidth: 560, p: 1 } }}
    >
      <DialogTitle sx={{ color: IPD_COLORS.textPrimary, pb: 0.5 }}>
        Review & Publish
        <Typography sx={{ fontSize: 13, fontWeight: 400, color: IPD_COLORS.textSecondary, mt: 0.5 }}>
          Confirm these details before publishing this RFQ.
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        <ReviewRow label="RFQ Number" value={rfqNumber} />
        <ReviewRow label="Title" value={title} />
        <ReviewRow label="Type" value={typeLabel} />
        <ReviewRow label="Issue Date" value={formatDateTime(issueDate)} />
        <ReviewRow label="Submission Deadline" value={formatDateTime(submissionDeadline)} />
        <ReviewRow
          label="Minimum Quotes"
          value={Number(minimumQuotes) < 1 ? 3 : minimumQuotes}
        />
        <Divider sx={{ my: 1, borderColor: IPD_COLORS.border }} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, py: 1 }}>
          <Chip
            label={`${validLineCount} line item${validLineCount !== 1 ? 's' : ''}`}
            size="small"
            sx={{ fontWeight: 700, backgroundColor: IPD_COLORS.selectedBg, color: IPD_COLORS.primary }}
          />
          <Chip
            label={`${vendorCount} vendor${vendorCount !== 1 ? 's' : ''} invited`}
            size="small"
            sx={{ fontWeight: 700, backgroundColor: IPD_COLORS.success.bg, color: IPD_COLORS.success.text }}
          />
          <Chip
            label={isOpenBid ? 'Open bid — new vendors' : 'Invited vendors only'}
            size="small"
            sx={{ fontWeight: 700 }}
          />
          <Chip
            label={`${attachmentCount} attachment(s)`}
            size="small"
            sx={{ fontWeight: 700 }}
          />
        </Box>
        {itemLines.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: IPD_COLORS.textSecondary, mb: 0.5 }}>
              Items
            </Typography>
            {itemLines.map((line, i) => (
              <Typography key={i} sx={{ fontSize: 13, color: IPD_COLORS.textPrimary }}>
                {line}
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <InvButton variant="outlined" tone="teal" onClick={onClose} disabled={loading}>
          Back
        </InvButton>
        <InvButton
          type="button"
          form={formId}
          tone="mint"
          loading={loading}
          onClick={(e) => {
            e.preventDefault();
            onPublish?.();
          }}
        >
          Publish RFQ
        </InvButton>
      </DialogActions>
    </Dialog>
  );
}
